import { type PropsWithChildren } from 'react'
import { useAccessTokenState } from './hooks/internal/useAccessTokenState'
import { useRefreshAccessTokenState } from './hooks/internal/useAutoRefreshAccessToken'
import { useGenerateActions } from './hooks/internal/useGenerateActions'
import { useRefreshTokenState } from './hooks/internal/useRefreshTokenState'
import { AccessTokenProvider } from './hooks/useAccessToken'
import { ApiAuthActionsProvider } from './hooks/useApiAuthActions'
import { useStorageAdapter } from './hooks/utility/useStorageAdapter'
import type { AuthConfig, AuthProviderProps } from './types'
import { IsAuthenticatedProvider } from './hooks/useIsAuthenticated'

export const ApiAuthProvider = (
  props: PropsWithChildren<AuthProviderProps>,
) => {
  const { children, storage, ...rest } = props

  const defaultAccessTokenStorage = useStorageAdapter(sessionStorage, 'cs_access_token')
  const [accessTokenRef, setAccessToken] = useAccessTokenState(
    storage?.accessToken || defaultAccessTokenStorage,
  )
  const defaultRefreshTokenStorage = useStorageAdapter(localStorage, 'cs_refresh_token')
  const [getRefreshToken, setRefreshToken] = useRefreshTokenState(
    storage?.refreshToken || defaultRefreshTokenStorage,
  )

  const config: AuthConfig = {
    accessTokenRef,
    setAccessToken,
    getRefreshToken,
    setRefreshToken,
    ...rest,
  }

  const actions = useGenerateActions(config)

  useRefreshAccessTokenState(accessTokenRef, actions.fetchAccessToken)

  // ApiAuthActions is non-reactive, memoized value, and should only
  // change when config changes
  //
  // AccessTokenProvider is a stateful / reactive value, so keep it as
  // a separate context so that consumers needing the static action
  // methods can avoid rerenders
  return (
    <ApiAuthActionsProvider value={actions}>
      <IsAuthenticatedProvider value={Boolean(getRefreshToken())}>
        <AccessTokenProvider value={accessTokenRef?.current?.token}>
          {children}
        </AccessTokenProvider>
      </IsAuthenticatedProvider>
    </ApiAuthActionsProvider>
  )
}
